import { Component } from '@theme/component';

/**
 * One featured-product testimonial card.
 *
 * Its only job is the review's read more / show less. The clamp is applied here rather than in
 * the markup so that, with no JS, the full review is on the page — a clamp with no way to
 * expand it would just be lost content.
 *
 * One component per card rather than one per section, so it works unchanged whether the card is
 * rendered on its own or inside a slideshow slide.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} quote - The review text.
 * @property {HTMLButtonElement} toggle - The read more / show less button.
 *
 * @extends {Component<Refs>}
 */
class ConcernTestimonialCard extends Component {
  requiredRefs = ['quote', 'toggle'];

  /** @type {ResizeObserver | null} */
  #observer = null;

  connectedCallback() {
    super.connectedCallback();

    this.dataset.clamped = 'true';

    // The clamp has to be in effect before the overflow is measurable, so wait a frame.
    requestAnimationFrame(() => this.#measure());

    // A card that reflows — carousel resize, font swap, orientation change — may cross the
    // threshold in either direction.
    this.#observer = new ResizeObserver(() => this.#measure());
    this.#observer.observe(this.refs.quote);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#observer?.disconnect();
    this.#observer = null;
  }

  /** Wired up declaratively with `on:click="/toggle"`. */
  toggle() {
    const expanded = this.dataset.expanded === 'true';

    if (expanded) delete this.dataset.expanded;
    else this.dataset.expanded = 'true';

    this.refs.toggle.setAttribute('aria-expanded', expanded ? 'false' : 'true');
    this.refs.toggle.textContent = expanded
      ? (this.dataset.moreLabel ?? '')
      : (this.dataset.lessLabel ?? '');
  }

  /** Shows the toggle only when the review is actually longer than its clamp. */
  #measure() {
    if (!this.isConnected) return;

    // While expanded there is no overflow to measure, and the toggle is needed regardless.
    if (this.dataset.expanded === 'true') return;

    const { quote } = this.refs;
    const overflows = quote.scrollHeight > quote.clientHeight + 1;

    if (overflows) this.dataset.expandable = 'true';
    else delete this.dataset.expandable;
  }
}

if (!customElements.get('concern-testimonial-card')) {
  customElements.define('concern-testimonial-card', ConcernTestimonialCard);
}
